import React from "react";
import {
  hasCustomTags,
  TagGroup,
  splitTextByTagGroups,
  ParsedDimensionTag,
  ParsedPersonMention,
  ParsedFileTag,
  ParsedReferenceTag,
  AnyParsedTag,
  TagType,
} from "@/lib/dimension-tag-parser";
import { AssessmentSurveyType } from "@/shared/constants/assessmentSurveys";
import { getNormalizedDimensionGroup } from "@/shared/utils/selectors/getDimensionTypes";
import { DimensionWithScores, PersonScore } from "@/features/people";
import { Attachment, Person } from "@/features/auth/graphql/generated";
import DimensionHighlight from "./DimensionHighlight";
import DimensionTag from "./DimensionTag";
import PersonMention from "./PersonMention";
import FileMention from "./FileMention";
import { first } from "lodash";
import { Pill } from "@/shared/ui";

export interface CustomTagsData {
  dimensions?: DimensionWithScores[];
  people?: Person[];
  attachments?: Attachment[]; // adjust based on actual Attachment type
  forceGrouping?: boolean; // Forces tags to be displayed in groups. Default is true
}

/**
 * Renders a single dimension tag component with the provided data
 */
const renderDimensionTag = (
  tag: ParsedDimensionTag,
  data: CustomTagsData | undefined,
  key: string
): React.ReactElement | null => {
  const dimensions = data?.dimensions || [];
  const dimension = dimensions.find((dim) => dim.id === tag.dim_id);

  const normalizedGroup = getNormalizedDimensionGroup(
    dimension?.group as AssessmentSurveyType
  );

  if (!dimension) {
    return null; // no dimension found
  }

  if (tag.highlight) {
    return (
      <DimensionHighlight
        key={key}
        dim_id={tag.dim_id}
        label={tag.text || dimension?.title || ""}
        score={dimension?.score as PersonScore}
        group={normalizedGroup}
      />
    );
  }

  return (
    <DimensionTag
      key={key}
      dim_id={tag.dim_id}
      label={tag.text || dimension?.title || ""}
      score={dimension?.score as PersonScore}
      group={normalizedGroup}
    />
  );
};

/**
 * Renders a person mention component with the provided data
 */
const renderPersonMention = (
  tag: ParsedPersonMention,
  data: CustomTagsData | undefined,
  key: string
): React.ReactElement => {
  const people = data?.people || [];
  const person = people.find((p) => p.id === tag.person_id);

  return (
    <PersonMention
      key={key}
      name={tag.person_name}
      avatar={person?.avatar || undefined}
      initials={person?.initials || undefined}
    />
  );
};

/**
 * Renders a file mention component with the provided data
 */
const renderFileMention = (
  tag: ParsedFileTag,
  data: CustomTagsData | undefined,
  key: string
): React.ReactElement => {
  const attachments = data?.attachments || [];
  const attachment = attachments.find((a) => a.id === tag.file_id);

  return (
    <FileMention
      key={key}
      name={attachment?.file_name || tag.file_name}
      file_id={tag.file_id}
      tagLabel={
        first(attachment?.tags || [])
          ?.split("_")
          ?.join(" ") || undefined
      }
      description={attachment?.description || undefined}
    />
  );
};

/**
 * Converts a tag type to a human-readable label
 * e.g., "ASSESSMENT_REPORT" -> "Assessment Report"
 */
const formatTagTypeAsLabel = (tagType: string): string => {
  return tagType
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Renders a reference tag (ASSESSMENT_REPORT, ASSESSMENT_PSYCH, PROJECT_DESCRIPTION) as a Pill
 */
const renderReferenceTag = (
  tag: ParsedReferenceTag,
  key: string
): React.ReactElement => {
  const label = formatTagTypeAsLabel(tag.type);

  return (
    <Pill
      key={key}
      variant="filled"
      color="grey"
      label={label}
      className="cursor-default"
    />
  );
};

/**
 * Renders a single parsed tag and returns the appropriate React component
 */
const renderSingleTag = (
  tag: AnyParsedTag,
  data: CustomTagsData | undefined,
  key: string
): React.ReactElement | null => {
  switch (tag.type) {
    case TagType.DIMENSION:
      return renderDimensionTag(tag, data, key);
    case TagType.PERSON:
      return renderPersonMention(tag, data, key);
    case TagType.FILE:
      return renderFileMention(tag, data, key);
    case TagType.ASSESSMENT_REPORT:
    case TagType.ASSESSMENT_PSYCH:
    case TagType.PROJECT_DESCRIPTION:
      return renderReferenceTag(tag as ParsedReferenceTag, key);
    default:
      // Fallback - should never reach here with proper typing
      return <span key={key}>{JSON.stringify(tag)}</span>;
  }
};

/**
 * Renders a tag group (which may contain multiple tags) inline.
 * References render as individual tags rather than a link that opens a drawer.
 */
const renderTagGroup = (
  tagGroup: TagGroup,
  data: CustomTagsData | undefined,
  keyPrefix: string
): React.ReactElement | null => {
  if (tagGroup.tags.length === 1) {
    return renderSingleTag(tagGroup.tags[0], data, `${keyPrefix}-single`);
  }

  return (
    <React.Fragment key={`${keyPrefix}-group`}>
      {tagGroup.tags.map((tag, index) =>
        renderSingleTag(tag, data, `${keyPrefix}-tag-${index}`)
      )}
    </React.Fragment>
  );
};

/**
 * Processes a text string that contains custom tags, splitting it and rendering components
 * Groups consecutive tags together (even when separate) and renders them inline
 */
const processTextWithTags = (
  text: string,
  data: CustomTagsData | undefined,
  keyPrefix: string
): React.ReactNode[] => {
  // Use the tag group parser to split text by tags
  const parts = splitTextByTagGroups(text);
  let keyCounter = 0;
  const result: React.ReactNode[] = [];
  let currentTagGroup: TagGroup | null = null;

  const flushCurrentTagGroup = () => {
    if (currentTagGroup && currentTagGroup.tags.length > 0) {
      result.push(
        renderTagGroup(currentTagGroup, data, `${keyPrefix}-${keyCounter++}`)
      );
      currentTagGroup = null;
    }
  };

  parts.forEach((part) => {
    if (typeof part === "object") {
      // This is a TagGroup - add its tags to current group
      if (!currentTagGroup) {
        currentTagGroup = part;
      } else {
        // Add tags from this group to current group
        currentTagGroup.tags.push(...part.tags);
        currentTagGroup.originalText += " " + part.originalText;
      }
    } else {
      // This is regular text
      // Check if it's just whitespace or comma(s) between tags
      const isSeparatorOnly = /^[\s,]*$/.test(part);

      if (isSeparatorOnly && currentTagGroup) {
        // Whitespace or comma between tags - continue current group
        // Don't flush yet
      } else if (currentTagGroup && !isSeparatorOnly) {
        // Non-separator text after tags - flush current group first
        flushCurrentTagGroup();
        result.push(part);
      } else {
        // No current group, just add the text
        result.push(part);
      }
    }
  });

  // Flush any remaining tag group
  flushCurrentTagGroup();

  return result;
};

/**
 * Processes a React element by recursively processing its children while preserving the element structure
 */
const processReactElement = (
  element: React.ReactElement,
  data: CustomTagsData | undefined,
  keyPrefix: string,
  keyCounter: number
): React.ReactElement => {
  const processedChildren = processChildrenRecursively(
    (element.props as any).children,
    data,
    `${keyPrefix}-${keyCounter}`
  );

  return React.cloneElement(element, {
    key: `${keyPrefix}-element-${keyCounter}`,
    children: processedChildren,
  } as any);
};

/**
 * Recursively processes children while preserving formatting and handling custom tags
 */
const processChildrenRecursively = (
  children: any,
  data: CustomTagsData | undefined,
  keyPrefix: string = ""
): React.ReactNode[] => {
  if (!children) return [];

  const result: React.ReactNode[] = [];
  let keyCounter = 0;

  const processChild = (child: any): React.ReactNode | React.ReactNode[] => {
    if (typeof child === "string") {
      // Only process string children that contain custom tags
      if (hasCustomTags(child)) {
        return processTextWithTags(
          child,
          data,
          `${keyPrefix}-text-${keyCounter++}`
        );
      }
      // Return string as-is if no custom tags
      return child;
    }

    if (child && typeof child === "object" && child.props) {
      // This is a React element (like <strong>, <em>, etc.)
      // Recursively process its children while preserving the element structure
      return processReactElement(child, data, keyPrefix, keyCounter++);
    }

    return child;
  };

  if (Array.isArray(children)) {
    children.forEach((child) => {
      const processed = processChild(child);
      if (Array.isArray(processed)) {
        result.push(...processed);
      } else {
        result.push(processed);
      }
    });
  } else {
    const processed = processChild(children);
    if (Array.isArray(processed)) {
      result.push(...processed);
    } else {
      result.push(processed);
    }
  }

  return result;
};

export const createMarkdownComponents = (data?: CustomTagsData) => {
  /**
   * Helper function to create a component that processes children for tags
   * This reduces code duplication across all markdown components
   * Defined inside to properly capture the data parameter
   */
  const createProcessingComponent = (Component: string, className: string) => {
    return ({ node, children, ...props }: any) => {
      // Process children recursively to handle tags
      const processedChildren = processChildrenRecursively(
        children,
        data,
        `${Component}`
      );

      return React.createElement(
        Component,
        {
          ...props,
          className: props.className
            ? `${props.className} ${className}`
            : className,
        },
        processedChildren
      );
    };
  };

  return {
    /**
     * Custom paragraph component that processes custom tags while preserving formatting
     *
     * This component handles:
     * - Dimension tags: [DIMENSION:id:text]
     * - Person mentions: [PERSON:id:name]
     * - File mentions: [FILE:id:name]
     * - Dimension highlights: [DIMENSION_HIGHLIGHT:id:text]
     *
     * It preserves all React formatting elements like <strong>, <em>, etc.
     */
    p: createProcessingComponent("p", "text-gray-700 leading-relaxed"),

    // Override default components with custom styling - all process tags now
    h1: createProcessingComponent(
      "h1",
      "text-2xl font-bold text-gray-900 mb-4 mt-6"
    ),
    h2: createProcessingComponent(
      "h2",
      "text-xl font-bold text-gray-900 mb-3 mt-5"
    ),
    h3: createProcessingComponent(
      "h3",
      "text-lg font-semibold text-gray-900 mb-2 mt-4"
    ),
    strong: ({ node, ...props }: any) => (
      <strong className="font-semibold text-gray-900" {...props} />
    ),
    code: ({ node, ...props }: any) => (
      <code
        className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-sm font-mono"
        {...props}
      />
    ),
    pre: ({ node, ...props }: any) => (
      <pre
        className="bg-gray-100 text-gray-800 p-4 rounded-lg overflow-x-auto mb-4"
        {...props}
      />
    ),
    blockquote: createProcessingComponent(
      "blockquote",
      "border-l-4 border-gray-300 pl-4 italic text-gray-600 mb-4"
    ),
    ul: ({ node, ...props }: any) => (
      <ul
        className="list-disc list-outside mb-4 space-y-1 pl-4 [&>li::marker]:text-gray-600"
        {...props}
      />
    ),
    ol: ({ node, ...props }: any) => (
      <ol
        className="list-decimal list-outside mb-4 space-y-1 pl-4"
        {...props}
      />
    ),
    li: createProcessingComponent("li", "text-gray-700"),
    a: ({ node, ...props }: any) => (
      <a
        className="text-primary! hover:text-primary-dark underline"
        {...props}
      />
    ),
    img: ({ node, ...props }: any) => (
      <img className="max-w-full h-auto rounded-lg mb-4" {...props} />
    ),
    table: ({ node, ...props }: any) => (
      <table
        className="w-full border-collapse border border-gray-300 mb-4"
        {...props}
      />
    ),
    th: createProcessingComponent(
      "th",
      "border border-gray-300 px-4 py-2 bg-gray-100 font-semibold text-left"
    ),
    td: createProcessingComponent("td", "border border-gray-300 px-4 py-2"),
    br: ({ node, ...props }: any) => {
      // Handle br tags properly - ensure they're self-closing and don't have children
      const { children, ...restProps } = props;
      return <br {...restProps} />;
    },
  };
};
